import React, { useCallback, useMemo, useState, useEffect } from "react";
import { MaterialReactTable } from "material-react-table";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  InputLabel,
  Select,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  ThemeProvider,
  createTheme,
  Chip,
  CircularProgress,
  Pagination,
} from "@mui/material";
import DateUtils from "../../Helpers/DateUtils";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Delete, Edit, FilterList } from "@mui/icons-material";
import { data, categories_template } from "./makeData.ts";
import AxiosHelper from "../../Helpers/AxiosHelper";
import Navbar from "../app/Navbar";
import AddIcon from "@mui/icons-material/Add";
import { checkLogin } from "../../Helpers/AuthHelper";
import { useNavigate } from "react-router-dom";
import { LOGIN_SCREEN_ROUTE, RELATIVE_EXPENSE_API_PATH } from "../../constants/ExpenseManagerConstants.js";

const ListExpenseMui = (props) => {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [tableData, setTableData] = useState(() => data);
  const [filteredData, setFilteredData] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [theme, setTheme] = useState("light");
  const [usersList, setUsersList] = useState([]);
  const [selectedUser, setSelectedUser] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  
  // Pagination states
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [totalItems, setTotalItems] = useState(0);
  
  const baseUrl = process.env.REACT_APP_API_BASEURL + RELATIVE_EXPENSE_API_PATH;
  const navigate = useNavigate();
  var categories = categories_template;

  useEffect(() => {
    const isUserLoggedIn = checkLogin();
    if (!isUserLoggedIn) {
      navigate(LOGIN_SCREEN_ROUTE);
    } else {
      fetchExpenses();
    }
  }, [navigate, page, pageSize]); // Re-fetch when page or pageSize changes

  const fetchExpenses = () => {
    setIsLoading(true);
    // Add pagination parameters to the API request
    const url = `${baseUrl}?page=${page}&size=${pageSize}`;
    
    AxiosHelper.getData(url)
      .then((response) => {
        const formattedData = response.data.expenses.map((expense) => {
          var dateToFormat = expense.expensedate;
          expense.expensedate = DateUtils.formatDate(dateToFormat);
          return expense;
        });
        
        setTableData(formattedData);
        setFilteredData(formattedData);
        setTotalPages(response.data.totalPages);
        setTotalItems(response.data.totalItems);
        
        // Extract unique users for filtering
        const users = [...new Set(formattedData.map(item => item.created_by))].filter(Boolean);
        setUsersList(users);
      })
      .catch((error) => {
        toast.error("Failed to load data!");
        console.error("Error fetching expenses:", error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (selectedUser === 'all') {
      setFilteredData(tableData);
    } else {
      const filtered = tableData.filter(item => item.created_by === selectedUser);
      setFilteredData(filtered);
    }
  }, [selectedUser, tableData]);

  var categoriesLocalStorage = localStorage.getItem("categories");
  if (categoriesLocalStorage) {
    categories = categoriesLocalStorage.split(",");
  }

  const darkTheme = createTheme({
    palette: {
      mode: theme,
    },
  });

  const changeTheme = () => {
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage - 1); // MaterialUI Pagination is 1-indexed, but our API is 0-indexed
  };

  const handlePageSizeChange = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing page size
  };

  const handleCreateNewRow = async (values) => {
    var newExpense = {
      expensedate: values.expensedate,
      category: values.category,
      description: values.description,
      amount: values.amount,
    };

    var { data } = await AxiosHelper.postData(baseUrl, newExpense);
    var toastMessage = "Failed to add expense. Please try again!";

    if (data?.success === true) {
      toastMessage = "Expense with description " + data.data.description + " added successfully!";
      toast.success(toastMessage, {
        position: "bottom-center",
        autoClose: 1500,
      });
    } else {
      toast.error(toastMessage);
    }

    setTimeout(() => fetchExpenses(), 1400);
  };

  const handleSaveRowEdits = async ({ exitEditingMode, row, values }) => {
    if (!Object.keys(validationErrors).length) {
      const updateUrl = baseUrl + "/" + values.id;
      AxiosHelper.putData(updateUrl, values);
      exitEditingMode();
      toast.success("Expense edited successfully!", {
        position: "bottom-center",
        autoClose: 1400,
      });
      setTimeout(() => fetchExpenses(), 1400);
    }
  };

  const handleCancelRowEdits = () => {
    setValidationErrors({});
  };

  const handleDeleteRow = useCallback(
    (row) => {
      if (!window.confirm(`Are you sure you want to delete ${row.getValue("description")}`)) return;
      const deleteUrl = baseUrl + "/" + row.original.id;
      AxiosHelper.deleteData(deleteUrl);
      toast.success("Expense deleted successfully!", {
        position: "bottom-center",
        autoClose: 1400,
      });
      setTimeout(() => fetchExpenses(), 1400);
    },
    []
  );

  const handleUserFilterChange = (userId) => {
    setSelectedUser(userId);
    setFilterModalOpen(false);
  };

  const clearFilter = () => {
    setSelectedUser('all');
  };

  const getCommonEditTextFieldProps = useCallback(
    (cell) => ({
      error: !!validationErrors[cell.id],
      helperText: validationErrors[cell.id],
      onBlur: (event) => {
        const isValid = cell.column.id === "email"
          ? validateEmail(event.target.value)
          : cell.column.id === "age"
            ? validateAge(+event.target.value)
            : validateRequired(event.target.value);
        if (!isValid) {
          setValidationErrors({
            ...validationErrors,
            [cell.id]: `${cell.column.columnDef.header} is required`,
          });
        } else {
          delete validationErrors[cell.id];
          setValidationErrors({ ...validationErrors });
        }
      },
    }),
    [validationErrors]
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: "id",
        header: "ID",
        enableColumnOrdering: false,
        enableEditing: false,
        enableSorting: false,
        size: 80,
      },
      {
        accessorKey: "expensedate",
        header: "Expense Date",
        size: 140,
        muiTableBodyCellEditTextFieldProps: ({ cell }) => ({
          type: "date"
        }),
      },
      {
        accessorKey: "amount",
        header: "Expense Amount",
        size: 140,
        muiTableBodyCellEditTextFieldProps: ({ cell }) => ({
          type: "number",
        }),
      },
      {
        accessorKey: "description",
        header: "Expense Description",
        muiTableBodyCellEditTextFieldProps: ({ cell }) => ({
          ...getCommonEditTextFieldProps(cell),
          type: "text",
        }),
      },
      {
        accessorKey: "category",
        header: "Expense Category",
        muiTableBodyCellEditTextFieldProps: {
          select: true,
          children: categories.map((state) => (
            <MenuItem key={state} value={state}>
              {state}
            </MenuItem>
          )),
        },
      },
      {
        accessorKey: "created_by",
        header: "Created By",
        enableEditing: false,
      },
    ],
    [getCommonEditTextFieldProps]
  );

  return (
    <ThemeProvider theme={darkTheme}>
      <Navbar />
      <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 2 }}>
        {selectedUser !== 'all' && (
          <Chip 
            label={`Filtered by: ${selectedUser}`} 
            onDelete={clearFilter} 
            color="primary" 
            sx={{ ml: 2 }}
          />
        )}
      </Box>

      {/* Loading Spinner */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <MaterialReactTable
            displayColumnDefOptions={{
              "mrt-row-actions": {
                muiTableHeadCellProps: { align: "center" },
                size: 120,
              },
            }}
            columns={columns}
            data={filteredData}
            editingMode="modal"
            enableColumnOrdering
            enableEditing
            onEditingRowSave={handleSaveRowEdits}
            onEditingRowCancel={handleCancelRowEdits}
            renderRowActions={({ row, table }) => (
              <Box sx={{ display: "flex", gap: "1rem" }}>
                <Tooltip arrow placement="left" title="Edit">
                  <IconButton onClick={() => table.setEditingRow(row)}>
                    <Edit />
                  </IconButton>
                </Tooltip>
                <Tooltip arrow placement="right" title="Delete">
                  <IconButton color="error" onClick={() => handleDeleteRow(row)}>
                    <Delete />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            renderTopToolbarCustomActions={() => (
              <Box sx={{ display: "flex", gap: "1rem" }}>
                <Button color="secondary" onClick={() => setCreateModalOpen(true)} variant="contained">
                  <AddIcon />
                </Button>
                <Button
                  color="primary"
                  onClick={() => setFilterModalOpen(true)}
                  variant="contained"
                  startIcon={<FilterList />}
                >
                  Filter By User
                </Button>
              </Box>
            )}
            initialState={{
              sorting: [{ id: "expensedate", desc: true }],
              columnVisibility: { created_by: false },
            }}
            muiToolbarAlertBannerProps={
              isLoading
                ? {
                    color: 'info',
                    children: 'Loading data',
                  }
                : undefined
            }
            state={{
              isLoading
            }}
            // Disable MaterialReactTable's built-in pagination since we're implementing our own
            enablePagination={false}
            manualPagination={true}
          />
          
          {/* Custom Pagination Component */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            p: 2,
            borderTop: '1px solid #e0e0e0'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControl variant="outlined" size="small" sx={{ mr: 2, minWidth: 120 }}>
                <InputLabel id="rows-per-page-label">Rows per page</InputLabel>
                <Select
                  labelId="rows-per-page-label"
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  label="Rows per page"
                >
                  {[5, 10, 25, 50].map((size) => (
                    <MenuItem key={size} value={size}>
                      {size}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="body2" color="textSecondary">
                {`${page * pageSize + 1}-${Math.min((page + 1) * pageSize, totalItems)} of ${totalItems}`}
              </Typography>
            </Box>
            <Pagination 
              count={totalPages}
              page={page + 1} // Convert from 0-indexed to 1-indexed
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        </>
      )}

      <CreateNewExpenseModal
        columns={columns}
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSubmit={handleCreateNewRow}
      />
      <FilterUserModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        users={usersList}
        selectedUser={selectedUser}
        onUserSelect={handleUserFilterChange}
      />
      <ToastContainer position="bottom-center" autoClose={5000} />
    </ThemeProvider>
  );
};

const FilterUserModal = ({ open, onClose, users, selectedUser, onUserSelect }) => {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Filter Expenses by User</DialogTitle>
      <DialogContent>
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel id="user-filter-label">Select User</InputLabel>
          <Select
            labelId="user-filter-label"
            id="user-filter"
            value={selectedUser}
            label="Select User"
            onChange={(e) => onUserSelect(e.target.value)}
          >
            <MenuItem value="all">All Users</MenuItem>
            {users.map((user) => (
              <MenuItem key={user} value={user}>
                {user}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={() => onUserSelect('all')} color="primary">
          Clear Filter
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const CreateNewExpenseModal = ({ open, columns, onClose, onSubmit }) => {
  var categories = localStorage.getItem("categories");
  if (!categories) {
    categories = categories_template;
  } else {
    categories = localStorage.getItem("categories").split(",");
  }
  const [values, setValues] = useState(() =>
    columns.reduce((acc, column) => {
      acc[column.accessorKey ?? ""] = "";
      return acc;
    }, {})
  );

  const [datePickerValue, setDatePickerValue] = useState();
  let Error = false;
  const dateNow = new Date();
  const year = dateNow.getFullYear();
  const monthWithOffset = dateNow.getUTCMonth() + 1;
  const month =
    monthWithOffset.toString().length < 2
      ? `0${monthWithOffset}`
      : monthWithOffset;
  const date =
    dateNow.getUTCDate().toString().length < 2
      ? `0${dateNow.getUTCDate()}`
      : dateNow.getUTCDate();

  const materialDateInput = `${year}-${month}-${date}`;
  const handleSubmit = () => {

    if (values.expensedate === '') {
        values.expensedate = materialDateInput;
    }

    if (values.amount === '') {
      toast.error("No amount found!")
      Error = true

    }

    if (values.description === '') {
      toast.error("No description found!")
      Error = true

    }

    if (values.category === '') {
      toast.error("No category found!")
      Error = true
    }

    if (Error === false) {
      onSubmit(values);
      onClose()
      setValues(() =>
        columns.reduce((acc, column) => {
          acc[column.accessorKey ?? ""] = "";
          return acc;
        }, {}))
    }
  }
  
  return (
    <Dialog open={open}>
      <DialogTitle textAlign="center" sx={{ color: "auto" }}>
        Create New Expense
      </DialogTitle>
      <DialogContent>
        <form onSubmit={(e) => e.preventDefault()}>
          <Stack
            sx={{
              width: "100%",
              minWidth: { xs: "300px", sm: "360px", md: "400px" },
              gap: "1.5rem",
            }}
          >
            {columns.map((column) => {
              if (column.accessorKey === "category") {
                return (
                  <FormControl fullWidth key={column.accessorKey}>
                    <InputLabel id="demo-simple-select-label">
                      Category
                    </InputLabel>
                    <Select
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      label={column.header}
                      name={column.accessorKey}
                      onChange={(e) =>
                        setValues({
                          ...values,
                          [e.target.name]: e.target.value,
                        })
                      }
                      displayEmpty
                    >
                      {categories.map((category) => {
                        return (
                          <MenuItem key={category} value={category}>
                            {category}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                );
              }
              if (column.accessorKey === "expensedate") {
                return (
                  <TextField
                    type="date"
                    key={column.accessorKey}
                    name={column.accessorKey}
                    onChange={(e) =>
                      setValues({ ...values, [e.target.name]: e.target.value })
                    }
                    defaultValue={materialDateInput}
                  />
                );
              }
              if (column.accessorKey === "amount") {
                return (
                  <TextField
                    type="number"
                    key={column.accessorKey}
                    label={column.header}
                    name={column.accessorKey}
                    onChange={(e) =>
                      setValues({ ...values, [e.target.name]: e.target.value })
                    }
                  />
                );
              }
              if (column.accessorKey === "id" || column.accessorKey === "created_by") {
                return (
                  <TextField
                    key={column.accessorKey}
                    label={column.header}
                    name={column.accessorKey}
                    disabled
                  />
                );
              } else {
                return (
                  <TextField
                    key={column.accessorKey}
                    label={column.header}
                    name={column.accessorKey}
                    onChange={(e) =>
                      setValues({
                        ...values,
                        [e.target.name]: e.target.value,
                      })
                    }
                  />
                );
              }
            })}
          </Stack>
        </form>
      </DialogContent>
      <DialogActions sx={{ p: "1.25rem" }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button color="secondary" onClick={handleSubmit} variant="contained">
          Create New Expense
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const validateRequired = (value) => !!value.length;
const validateEmail = (email) =>
  !!email.length &&
  email
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
const validateAge = (age) => age >= 18 && age <= 50;

export default ListExpenseMui;